import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData, SettlementDetails } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface CaliberFilterProps {
  data: MarketData[] | SettlementDetails[];
  selectedCaliber: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  defaultSelectAll?: boolean;
  hideAllOption?: boolean;
}

const CaliberFilter: React.FC<CaliberFilterProps> = ({
  data,
  selectedCaliber,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  defaultSelectAll = false,
  hideAllOption = false,
}) => {
  const { t } = useTranslation('common');
  const defaultLabel = label || t('Calibre');
  const [search, setSearch] = useState('');
  // Ref para evitar selecciones automáticas después de la primera
  const hasAutoSelectedRef = React.useRef(false);

  // Memoizar el procesamiento de datos para los calibres
  const caliberData = useMemo(() => {
    // Create a map to count records per caliber
    return data.reduce(
      (acc, item) => {
        // Manejar tipos de datos diferentes de forma segura
        const caliber =
          'size' in item && item.size
            ? item.size
            : 'caliber' in item && item.caliber
              ? item.caliber
              : undefined;
        if (caliber) {
          acc[caliber] = (acc[caliber] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [data]);

  // Memoizar la lista ordenada de calibres
  const calibers = useMemo(() => {
    // Get all calibers and sort them according to predefined order
    const order = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
    const allCalibers = Object.keys(caliberData);

    return allCalibers.sort((a, b) => {
      const indexA = order.indexOf(a);
      const indexB = order.indexOf(b);
      return indexA - indexB;
    });
  }, [caliberData]);

  const filteredCalibers = useMemo(() => {
    if (!search) return calibers;
    const lower = search.toLowerCase();
    return calibers.filter((c) => c.toLowerCase().includes(lower));
  }, [calibers, search]);

  // Memoizar el calibre más común (sin console.log)
  const mostCommonCaliber = useMemo(() => {
    let mostCommonCaliber = '';
    let maxCount = 0;

    Object.entries(caliberData).forEach(([caliber, count]) => {
      if (count > maxCount) {
        mostCommonCaliber = caliber;
        maxCount = count;
      }
    });

    return mostCommonCaliber;
  }, [caliberData]);

  // Memoizar los selected keys
  const selectedKeys = useMemo(() => {
    if (selectionMode === 'multiple') {
      return Array.isArray(selectedCaliber)
        ? new Set(selectedCaliber)
        : new Set([]);
    }
    return new Set([selectedCaliber as string]);
  }, [selectionMode, selectedCaliber]);

  // Memoizar el texto para la opción "Todos"
  const allOptionText = useMemo(() => {
    if (selectionMode === 'multiple') {
      return Array.isArray(selectedCaliber) && selectedCaliber.length > 0
        ? t('Deseleccionar todos')
        : t('Seleccionar todos');
    }
    return t('Todos');
  }, [selectionMode, selectedCaliber, t]);

  // Verificar cuando cambian los datos para potencialmente reiniciar la selección
  useEffect(() => {
    // Si los datos cambian significativamente, permitir una nueva selección automática
    if (data.length > 0 && calibers.length > 0) {
      hasAutoSelectedRef.current = false;
    }
  }, [data, calibers]);

  // Memoizar el handler de selección
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (selectionMode === 'multiple') {
        const keysArray = Array.from(keys);

        if (keysArray.includes('all')) {
          // If 'all' is selected and there were previous selections, clear all
          if (Array.isArray(selectedCaliber) && selectedCaliber.length > 0) {
            onSelectionChange([]);
          } else {
            // Otherwise, select all calibers
            onSelectionChange(calibers);
          }
        } else {
          // Normal selection behavior for individual items
          onSelectionChange(keysArray as string[]);
        }
      } else {
        // Keep existing behavior for single selection
        const selectedKeys = Array.from(keys);
        onSelectionChange(selectedKeys as string[]);
        // Marcar como seleccionado explícitamente
        hasAutoSelectedRef.current = true;
      }
    },
    [selectionMode, selectedCaliber, calibers, onSelectionChange]
  );

  // Efecto para selección automática inicial
  useEffect(() => {
    // Solo seleccionar automáticamente si no se ha hecho antes
    if (hasAutoSelectedRef.current) return;

    if (calibers.length === 0) return;

    // Si ya hay una selección o si hay valores por defecto, no hacer nada
    if (
      (Array.isArray(selectedCaliber) && selectedCaliber.length > 0) ||
      (typeof selectedCaliber === 'string' &&
        selectedCaliber !== '' &&
        selectedCaliber !== 'all') ||
      (defaultSelectedKeys && defaultSelectedKeys.length > 0)
    ) {
      hasAutoSelectedRef.current = true;
      return;
    }

    // Seleccionar automáticamente según el modo
    if (selectionMode === 'multiple' && defaultSelectAll) {
      // Modo múltiple con selectAll: seleccionar todos
      onSelectionChange(calibers);
      hasAutoSelectedRef.current = true;
      return;
    }

    // Si hay un calibre más común, seleccionarlo (tanto en modo single como multiple)
    if (mostCommonCaliber) {
      onSelectionChange([mostCommonCaliber]);
      hasAutoSelectedRef.current = true;
    }
  }, [
    calibers,
    mostCommonCaliber,
    selectedCaliber,
    selectionMode,
    defaultSelectAll,
    defaultSelectedKeys,
    onSelectionChange,
  ]);

  // No renderizar nada si no hay calibres
  if (calibers.length === 0) {
    return null;
  }

  // Renderizar elementos SelectItem para el componente
  const renderSelectItems = () => {
    const items = [];

    // Agregar opción "Todos" si corresponde
    if (selectionMode === 'single' && !hideAllOption) {
      items.push(
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      );
    }

    if (selectionMode === 'multiple' && !hideAllOption) {
      items.push(
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>{allOptionText}</span>
          </div>
        </SelectItem>
      );
    }

    // Agregar items para cada calibre
    filteredCalibers.forEach((caliber) => {
      items.push(
        <SelectItem key={caliber} value={caliber} textValue={caliber}>
          {caliber}
        </SelectItem>
      );
    });

    return items;
  };

  return (
    <Select
      label={defaultLabel}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={t('Selecciona calibre')}
      renderValue={(items) => {
        if (
          selectionMode === 'multiple' &&
          Array.isArray(selectedCaliber) &&
          selectedCaliber.length === calibers.length
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todos los calibres')}</div>
          );
        }
        if (Array.isArray(selectedCaliber) && selectedCaliber.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedCaliber.join(', ')}
            </div>
          );
        }
        return (
            <div className='flex items-center gap-2'>
              {items.map((item) => item.textValue).join(', ')}
            </div>
          );
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar calibre...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
    >
      {renderSelectItems()}
    </Select>
  );
};

// Usar una función de comparación personalizada para React.memo
export default React.memo(CaliberFilter, (prevProps, nextProps) => {
  // Re-renderizar si los props fundamentales cambian
  if (
    prevProps.selectionMode !== nextProps.selectionMode ||
    prevProps.hideAllOption !== nextProps.hideAllOption ||
    prevProps.defaultSelectAll !== nextProps.defaultSelectAll
  ) {
    return false;
  }

  // Comparación eficiente de arrays para selectedCaliber
  const prevSelected = Array.isArray(prevProps.selectedCaliber)
    ? prevProps.selectedCaliber.join(',')
    : prevProps.selectedCaliber;
  const nextSelected = Array.isArray(nextProps.selectedCaliber)
    ? nextProps.selectedCaliber.join(',')
    : nextProps.selectedCaliber;

  if (prevSelected !== nextSelected) {
    return false;
  }

  // Verificar si los datos han cambiado (comparación superficial)
  if (prevProps.data.length !== nextProps.data.length) {
    return false;
  }

  // Si la función de callback cambia, también re-renderizar
  if (prevProps.onSelectionChange !== nextProps.onSelectionChange) {
    return false;
  }

  // Si llegamos aquí, no ha habido cambios significativos
  return true;
});
